import React, { useCallback, useRef, useEffect, useState } from 'react';
import ForceGraph2D, { ForceGraphMethods, NodeObject } from 'react-force-graph-2d';
import { GraphData, GraphNode, GraphLink } from '../../types/graph';

interface NetworkGraphProps {
    data: GraphData;
    width?: number;
    height?: number;
}

type NodeWithCoords = GraphNode & {
    x?: number;
    y?: number;
};

interface OpacityMap {
    [key: string]: {
        current: number;
        target: number;
    };
}

const TRANSITION_DURATION = 200; // milliseconds

export function NetworkGraph({ data, width = 1200, height = 800 }: NetworkGraphProps) {
    const graphRef = useRef<ForceGraphMethods<GraphNode, GraphLink>>();
    const imagesCache = useRef<Map<string, HTMLImageElement>>(new Map());
    const [hoveredNode, setHoveredNode] = useState<string | null>(null);
    const [currentZoom, setCurrentZoom] = useState<number>(1);
    const [isZooming, setIsZooming] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const zoomTimeoutRef = useRef<NodeJS.Timeout>();
    const nodeOpacities = useRef<OpacityMap>({});
    const linkOpacities = useRef<OpacityMap>({});
    const animationFrameId = useRef<number>();
    const hasInitialFit = useRef(false);

    // Initialize or update opacity maps when data changes
    useEffect(() => {
        // Initialize node opacities
        data.nodes.forEach(node => {
            if (!nodeOpacities.current[node.id]) {
                nodeOpacities.current[node.id] = { current: 1, target: 1 };
            }
        });

        // Initialize link opacities
        data.links.forEach(link => {
            const sourceId = typeof link.source === 'string' ? link.source :
                (link.source as NodeWithCoords).id;
            const targetId = typeof link.target === 'string' ? link.target :
                (link.target as NodeWithCoords).id;
            const linkId = `${sourceId}-${targetId}`;
            if (!linkOpacities.current[linkId]) {
                linkOpacities.current[linkId] = { current: 0.5, target: 0.5 };
            }
        });
    }, [data]);

    // Animation loop for smooth transitions
    const animate = useCallback(() => {
        let needsUpdate = false;
        const lerp = (start: number, end: number, t: number) => start + (end - start) * t;
        const step = 1 / (TRANSITION_DURATION / 16); // 16ms is roughly one frame at 60fps

        // Update node opacities
        Object.keys(nodeOpacities.current).forEach(id => {
            const opacity = nodeOpacities.current[id];
            if (opacity.current !== opacity.target) {
                opacity.current = lerp(opacity.current, opacity.target, step);
                if (Math.abs(opacity.current - opacity.target) < 0.01) {
                    opacity.current = opacity.target;
                }
                needsUpdate = true;
            }
        });

        // Update link opacities
        Object.keys(linkOpacities.current).forEach(id => {
            const opacity = linkOpacities.current[id];
            if (opacity.current !== opacity.target) {
                opacity.current = lerp(opacity.current, opacity.target, step);
                if (Math.abs(opacity.current - opacity.target) < 0.01) {
                    opacity.current = opacity.target;
                }
                needsUpdate = true;
            }
        });

        if (needsUpdate && graphRef.current) {
            graphRef.current.d3ReheatSimulation();
        }

        if (needsUpdate) {
            animationFrameId.current = requestAnimationFrame(animate);
        }
    }, []);

    // Update opacity targets when hover state changes
    useEffect(() => {
        // Update node opacity targets
        data.nodes.forEach(node => {
            const opacity = nodeOpacities.current[node.id];
            if (opacity) {
                opacity.target = getNodeTargetOpacity(node.id);
            }
        });

        // Update link opacity targets
        data.links.forEach(link => {
            const sourceId = typeof link.source === 'string' ? link.source :
                (link.source as NodeWithCoords).id;
            const targetId = typeof link.target === 'string' ? link.target :
                (link.target as NodeWithCoords).id;
            const linkId = `${sourceId}-${targetId}`;
            const opacity = linkOpacities.current[linkId];
            if (opacity) {
                opacity.target = getLinkTargetOpacity(sourceId, targetId);
            }
        });

        // Start animation
        animationFrameId.current = requestAnimationFrame(animate);

        return () => {
            if (animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current);
            }
        };
    }, [hoveredNode, data, animate]);

    const getNodeTargetOpacity = useCallback((nodeId: string) => {
        if (!hoveredNode) return 1;
        if (nodeId === hoveredNode) return 1;

        const isConnected = data.links.some(link => {
            const source = link.source as NodeWithCoords | string;
            const target = link.target as NodeWithCoords | string;
            const sourceId = typeof source === 'string' ? source : source.id;
            const targetId = typeof target === 'string' ? target : target.id;
            return (sourceId === hoveredNode && targetId === nodeId) ||
                (targetId === hoveredNode && sourceId === nodeId);
        });

        return isConnected ? 1 : 0.2;
    }, [hoveredNode, data.links]);

    const getLinkTargetOpacity = useCallback((sourceId: string, targetId: string) => {
        if (!hoveredNode) return 0.5;
        return (sourceId === hoveredNode || targetId === hoveredNode) ? 0.8 : 0.1;
    }, [hoveredNode]);

    const getNodeOpacity = useCallback((nodeId: string) => {
        return nodeOpacities.current[nodeId]?.current ?? 1;
    }, []);

    const getLinkOpacity = useCallback((sourceId: string, targetId: string) => {
        const linkId = `${sourceId}-${targetId}`;
        return linkOpacities.current[linkId]?.current ?? 0.5;
    }, []);

    // Preload images
    useEffect(() => {
        data.nodes.forEach(node => {
            if (node.image && !imagesCache.current.has(node.image)) {
                const img = new Image();
                img.src = node.image;
                img.onload = () => {
                    imagesCache.current.set(node.image!, img);
                    if (graphRef.current) {
                        graphRef.current.d3ReheatSimulation();
                    }
                };
            }
        });
    }, [data.nodes]);

    const handleNodeClick = useCallback((node: GraphNode) => {
        if (!node.x || !node.y) return;

        if (graphRef.current) {
            graphRef.current.centerAt(node.x, node.y, 1000);
            graphRef.current.zoom(2.5, 1000);
        }
    }, []);

    const handleNodeHover = useCallback((node: GraphNode | null) => {
        setHoveredNode(node ? node.id : null);
    }, []);

    const handleZoom = useCallback(({ k }: { k: number }) => {
        setCurrentZoom(k);
        setIsZooming(true);

        if (!isHovering) {
            // Clear existing timeout
            if (zoomTimeoutRef.current) {
                clearTimeout(zoomTimeoutRef.current);
            }

            // Set new timeout to hide the control
            zoomTimeoutRef.current = setTimeout(() => {
                setIsZooming(false);
            }, 1500); // Hide after 1.5 seconds of inactivity
        }
    }, [isHovering]);

    const handleSliderChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const newZoom = parseFloat(event.target.value);
        if (graphRef.current) {
            graphRef.current.zoom(newZoom, 300);
        }
        setIsZooming(true);

        if (!isHovering) {
            if (zoomTimeoutRef.current) {
                clearTimeout(zoomTimeoutRef.current);
            }

            zoomTimeoutRef.current = setTimeout(() => {
                setIsZooming(false);
            }, 1500);
        }
    }, [isHovering]);

    const handleResetView = useCallback(() => {
        if (graphRef.current) {
            graphRef.current.zoomToFit(400, 50);
            setIsZooming(true);

            if (!isHovering) {
                if (zoomTimeoutRef.current) {
                    clearTimeout(zoomTimeoutRef.current);
                }

                zoomTimeoutRef.current = setTimeout(() => {
                    setIsZooming(false);
                }, 1500);
            }
        }
    }, [isHovering]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (zoomTimeoutRef.current) {
                clearTimeout(zoomTimeoutRef.current);
            }
        };
    }, []);

    const renderNode = useCallback((node: NodeWithCoords, ctx: CanvasRenderingContext2D, globalScale: number) => {
        if (!node.x || !node.y) return;

        const opacity = getNodeOpacity(node.id);
        const size = (node.size || 5) / globalScale;
        const fontSize = 12 / globalScale;
        const label = node.name;

        ctx.save();
        ctx.globalAlpha = opacity;

        // Add shadow
        ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
        ctx.shadowBlur = 5 / globalScale;
        ctx.shadowOffsetX = 2 / globalScale;
        ctx.shadowOffsetY = 2 / globalScale;

        // Draw node circle
        ctx.beginPath();
        ctx.arc(node.x, node.y, size, 0, 2 * Math.PI, false);
        ctx.fillStyle = node.color || '#fff';
        ctx.fill();

        // Reset shadow before drawing image and border
        ctx.shadowColor = 'transparent';

        // If image is available and loaded, draw it inside the circle
        if (node.image && imagesCache.current.has(node.image)) {
            ctx.save();
            ctx.clip();
            const img = imagesCache.current.get(node.image)!;
            const imgSize = size * 2;
            ctx.drawImage(
                img,
                node.x - size,
                node.y - size,
                imgSize,
                imgSize
            );
            ctx.restore();
        }

        // Draw border - thinner and more elegant
        ctx.strokeStyle = node.color || '#fff';
        ctx.lineWidth = 1 / globalScale;
        ctx.stroke();

        // Draw label
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.font = `${fontSize}px Inter, sans-serif`;
        const textWidth = ctx.measureText(label).width;

        // Add subtle shadow to text background
        ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
        ctx.shadowBlur = 3 / globalScale;
        ctx.shadowOffsetX = 1 / globalScale;
        ctx.shadowOffsetY = 1 / globalScale;

        // Draw background for text - more subtle
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.fillRect(
            node.x - textWidth / 2 - fontSize * 0.2,
            node.y + size + fontSize * 0.2,
            textWidth + fontSize * 0.4,
            fontSize + fontSize * 0.4
        );

        // Reset shadow for text
        ctx.shadowColor = 'transparent';

        // Draw text - softer color
        ctx.fillStyle = '#4B5563';
        ctx.fillText(label, node.x, node.y + size + fontSize + fontSize * 0.4);

        ctx.restore();
    }, [getNodeOpacity]);

    const renderLink = useCallback((link: GraphLink, ctx: CanvasRenderingContext2D, globalScale: number) => {
        const source = link.source as NodeWithCoords | string;
        const target = link.target as NodeWithCoords | string;

        const sourceNode = typeof source === 'string' ?
            data.nodes.find(n => n.id === source) as NodeWithCoords :
            source;
        const targetNode = typeof target === 'string' ?
            data.nodes.find(n => n.id === target) as NodeWithCoords :
            target;

        if (!sourceNode?.x || !sourceNode?.y || !targetNode?.x || !targetNode?.y) return;

        const sourceId = typeof source === 'string' ? source : source.id;
        const targetId = typeof target === 'string' ? target : target.id;

        const opacity = getLinkOpacity(sourceId, targetId);

        ctx.save();
        ctx.globalAlpha = opacity;
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)';
        ctx.lineWidth = 0.8 / globalScale;
        ctx.beginPath();
        ctx.moveTo(sourceNode.x, sourceNode.y);
        ctx.lineTo(targetNode.x, targetNode.y);
        ctx.stroke();
        ctx.restore();
    }, [data.nodes, getLinkOpacity]);

    useEffect(() => {
        // Adjust force simulation parameters when the graph is initialized
        if (graphRef.current) {
            graphRef.current.d3Force('charge')?.strength(-800);
            graphRef.current.d3Force('link')?.distance(150);
            graphRef.current.d3Force('center')?.strength(1);
        }
    }, []);

    // Add new effect to fit view to all nodes
    useEffect(() => {
        if (graphRef.current && !hasInitialFit.current && data.nodes.length > 0) {
            // Wait a bit for the force simulation to settle
            setTimeout(() => {
                if (graphRef.current) {
                    graphRef.current.zoomToFit(400, 50); // duration: 400ms, padding: 50px
                    hasInitialFit.current = true;
                }
            }, 500);
        }
    }, [data.nodes]);

    return (
        <div className="relative w-full h-full bg-white">
            <ForceGraph2D<GraphNode, GraphLink>
                ref={graphRef}
                graphData={data}
                nodeLabel="name"
                nodeColor={(node) => node.color || '#fff'}
                nodeVal={(node) => node.size || 5}
                linkColor={() => 'rgba(0, 0, 0, 0.2)'}
                linkWidth={0.8}
                onNodeClick={handleNodeClick}
                onNodeHover={handleNodeHover}
                onZoom={handleZoom}
                nodeCanvasObject={renderNode}
                linkCanvasObject={renderLink}
                width={width}
                height={height}
                cooldownTicks={100}
                backgroundColor="#ffffff"
                d3VelocityDecay={0.4}
                d3AlphaDecay={0.01}
                minZoom={0.5}
                maxZoom={2.5}
            />
            <div
                className={`absolute left-4 bottom-4 bg-white/90 px-4 py-2 rounded-lg shadow-sm border border-gray-200 transition-opacity duration-300 ${isZooming || isHovering ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
            >
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleResetView}
                        className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
                        title="Reset View"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-gray-600">
                            <path fillRule="evenodd" d="M4.755 10.059a7.5 7.5 0 0112.548-3.364l1.903 1.903h-3.183a.75.75 0 100 1.5h4.992a.75.75 0 00.75-.75V4.356a.75.75 0 00-1.5 0v3.18l-1.9-1.9A9 9 0 003.306 9.67a.75.75 0 101.45.388zm15.408 3.352a.75.75 0 00-.919.53 7.5 7.5 0 01-12.548 3.364l-1.902-1.903h3.183a.75.75 0 000-1.5H2.984a.75.75 0 00-.75.75v4.992a.75.75 0 001.5 0v-3.18l1.9 1.9a9 9 0 0015.059-4.035.75.75 0 00-.53-.918z" clipRule="evenodd" />
                        </svg>
                    </button>
                    <span className="text-sm text-gray-600 font-medium min-w-[45px]">
                        {Math.round(currentZoom * 100)}%
                    </span>
                    <input
                        type="range"
                        min="0.5"
                        max="2.5"
                        step="0.1"
                        value={currentZoom}
                        onChange={handleSliderChange}
                        className="w-32 h-2 appearance-none cursor-pointer
                            bg-gradient-to-r from-indigo-200 via-purple-200 to-pink-200
                            rounded-lg
                            [&::-webkit-slider-thumb]:appearance-none
                            [&::-webkit-slider-thumb]:h-4
                            [&::-webkit-slider-thumb]:w-4
                            [&::-webkit-slider-thumb]:rounded-full
                            [&::-webkit-slider-thumb]:bg-gradient-to-r
                            [&::-webkit-slider-thumb]:from-indigo-500
                            [&::-webkit-slider-thumb]:to-purple-500
                            [&::-webkit-slider-thumb]:shadow-md
                            [&::-webkit-slider-thumb]:cursor-pointer
                            [&::-moz-range-thumb]:appearance-none
                            [&::-moz-range-thumb]:h-4
                            [&::-moz-range-thumb]:w-4
                            [&::-moz-range-thumb]:rounded-full
                            [&::-moz-range-thumb]:bg-gradient-to-r
                            [&::-moz-range-thumb]:from-indigo-500
                            [&::-moz-range-thumb]:to-purple-500
                            [&::-moz-range-thumb]:border-0
                            [&::-moz-range-thumb]:shadow-md
                            [&::-moz-range-thumb]:cursor-pointer"
                    />
                </div>
            </div>
        </div>
    );
} 